import { useEffect, useRef } from 'react'
import { pageEvent } from '@/features/shared/analytics/pageEvent'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { useUserId } from '@/features/account/services/User/hooks'
import { usePathWithSearch } from '@/features/shared/hooks/routes/usePathWithSearch'
import { flushPciTrackingQueue } from '@/features/shared/analytics/google'

export const useRouteChangeTracking = () => {
  const userId = useUserId()
  const { isSessionLoading, isFullyAuthenticated } = useSession()

  useEffect(() => {
    // if session is loading, do not track page event
    if (isSessionLoading) return
    // if isFullyAuthenticated is true and user data is not available yet, do not track page event
    if (isFullyAuthenticated && !userId) return
    // track page event for initial app page load
    pageEvent({
      pathname: window.location.pathname,
      isInitialNavigation: true,
      previousUrl: undefined,
    })
    // this flushes the PCI tracking queue when a user lands on a non PCI page
    // via a hard navigation so that any pending PCI events are added to
    // window.dataLayer which enables proper tracking through GTM.
    // NOTE: it is important to only invoke this after the initial page event is fired
    // since there are GTM tags that depend on the page event firing in order to load correctly.
    flushPciTrackingQueue()
  }, [isSessionLoading, isFullyAuthenticated, userId])

  // invoke page event only when the url changes
  const currentUrl = usePathWithSearch()
  const previousUrl = useRef(currentUrl)
  useEffect(() => {
    if (previousUrl.current && previousUrl.current !== currentUrl) {
      pageEvent({
        pathname: window.location.pathname,
        previousUrl: previousUrl.current,
      })
      previousUrl.current = currentUrl
    }
  }, [currentUrl])
}
